// Phase 484 D-484-1 — native window.location.pathname + popstate routing.
// No third-party router dep (D-484-1b excludes it; 4 routes do not justify it).
// Modeled on cogo-web/apps/cogo-starter/src/App.tsx:361-389 TenantApp distilled;
// stripped of useCogoWebMCP / loadFromSchemaUrl / appV2 / toast (PATTERNS.md §App.tsx).
import { useState, useEffect, useCallback } from "react";
import { Landing } from "./pages/Landing";
import { Storefront } from "./pages/Storefront";
import { ProductDetail } from "./pages/ProductDetail";
import { Checkout } from "./pages/Checkout";
import { TokenDiffProbe } from "./pages/TokenDiffProbe";

// Tailwind compiler safelist for dynamic Cogo JSON classes (prevent tree-shaking)
const _tailwindSafelist = [
  "gap-1", "gap-[6px]", "gap-1.5", "gap-2", "gap-3", "gap-4", "gap-5", "gap-6", "gap-8", "gap-10", "gap-12", "gap-16", "gap-20", "gap-24", "gap-32",
  "grid-cols-1", "grid-cols-2", "grid-cols-3",
  "col-span-1", "col-span-2", "col-span-3",
  "md:grid-cols-2", "lg:grid-cols-3", "lg:grid-cols-2",
  "aspect-square", "aspect-[4/3]", "aspect-[16/10]",
  "rounded-lg", "rounded-xl", "rounded-2xl", "rounded-3xl", "rounded-full"
];

export const PRODUCTS = [
  {
    id: "product-1",
    name: "AeroMax Pro Sneaker",
    brand: "AEROMAX • PERFORMANCE RUNNING",
    price: 149.00,
    priceStr: "$149.00",
    image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=60",
    desc: "Ergonomic performance runner featuring oklch vivid detailing, breathable mesh weave, and responsive shock absorption."
  },
  {
    id: "product-2",
    name: "Vanguard Chronograph",
    brand: "VANGUARD • HOROLOGY",
    price: 289.00,
    priceStr: "$289.00",
    image: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&auto=format&fit=crop&q=60",
    desc: "Sleek premium mechanical timepiece with dual-subdial chronograph layout, scratch-resistant sapphire crystal, and an authentic brown Italian leather band."
  },
  {
    id: "product-3",
    name: "AeroSound Elite ANC",
    brand: "AEROSOUND • PREMIUM AUDIOPHILE",
    price: 199.00,
    priceStr: "$199.00",
    image: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&auto=format&fit=crop&q=60",
    desc: "The AeroSound Elite ANC delivers highly immersive high-resolution audio using custom dynamic audio drivers and advanced active noise cancellation technology."
  }
];

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
}

export function App() {
  const [route, setRoute] = useState<string>(
    typeof window !== "undefined" ? window.location.pathname : "/",
  );
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("product-3");
  const [activeThumbIndex, setActiveThumbIndex] = useState<number>(0);
  const [isCheckoutSuccess, setIsCheckoutSuccess] = useState<boolean>(false);

  useEffect(() => {
    const onPopState = () => setRoute(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((path: string) => {
    setRoute(path);
    window.history.pushState(null, "", path);
    window.scrollTo(0, 0);
  }, []);

  const addToCart = useCallback((productId: string) => {
    const prod = PRODUCTS.find((p) => p.id === productId);
    if (!prod) return;
    setCart((prev) => {
      const existing = prev.find((item) => item.id === productId);
      if (existing) {
        return prev.map((item) =>
          item.id === productId ? { ...item, quantity: item.quantity + 1 } : item,
        );
      }
      return [
        ...prev,
        { id: prod.id, name: prod.name, price: prod.price, image: prod.image, quantity: 1 },
      ];
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Trailing-slash normalization (RESEARCH §Native Routing Pattern Pitfalls).
  // Cloudflare Pages serves /storefront/ and /storefront identically; normalize
  // here so client-side dispatch is consistent with edge behavior.
  const normalized = route.replace(/\/+$/, "") || "/";

  if (normalized === "/storefront") {
    return (
      <Storefront
        cartCount={cart.reduce((acc, item) => acc + item.quantity, 0)}
        onNavigate={navigate}
        onAddToCart={addToCart}
        setSelectedProductId={setSelectedProductId}
      />
    );
  }
  if (normalized === "/product-detail") {
    return (
      <ProductDetail
        cartCount={cart.reduce((acc, item) => acc + item.quantity, 0)}
        onNavigate={navigate}
        onAddToCart={addToCart}
        selectedProductId={selectedProductId}
        activeThumbIndex={activeThumbIndex}
        setActiveThumbIndex={setActiveThumbIndex}
      />
    );
  }
  if (normalized === "/checkout") {
    return (
      <Checkout
        cart={cart}
        onNavigate={navigate}
        onClearCart={clearCart}
        isCheckoutSuccess={isCheckoutSuccess}
        setIsCheckoutSuccess={setIsCheckoutSuccess}
      />
    );
  }
  // Unlinked renderer-infrastructure route — the SC#3 token-diff Playwright
  // probe (Phase 486 D-486-08). Not a user-facing toggle; no nav/landing link.
  if (normalized === "/token-diff") return <TokenDiffProbe />;
  if (normalized === "/") return <Landing onNavigate={navigate} />;

  // 404 placeholder — Tailwind defaults, minimal text.
  return (
    <main className="p-8">
      <h1 className="text-xl font-bold">Not found</h1>
      <p className="mt-2 text-stone-600">
        v77 demo has 4 routes: /, /storefront, /product-detail, /checkout.
      </p>
    </main>
  );
}
